import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatGoogleMapsAutocompleteModule} from '@angular-material-extensions/google-maps-autocomplete';
import {of} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {LoadingService} from '@ui-kit/framework/util/service/loading.service';
import {SnackbarService} from '@ui-kit/framework/util/service/snackbar.service';
import {AddressService} from '@ui-kit/framework/util/service/address.service';
import {toAdminAddress} from '../../admin-address.util';
import {SetupService} from '../setup.service';
import PlaceResult = google.maps.places.PlaceResult;

export const COUNTRIES = [
  {code: 'ZA', name: 'South Africa'},
  {code: 'NA', name: 'Namibia'},
  {code: 'BW', name: 'Botswana'},
  {code: 'ZW', name: 'Zimbabwe'},
  {code: 'MZ', name: 'Mozambique'},
  {code: 'GB', name: 'United Kingdom'},
  {code: 'US', name: 'United States'},
];

@Component({
  selector: 'app-create-landlord-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatGoogleMapsAutocompleteModule,
  ],
  templateUrl: './create-landlord-dialog.component.html',
})
export class CreateLandlordDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<CreateLandlordDialogComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly setupService = inject(SetupService);
  private readonly addressService = inject(AddressService);
  private readonly snackbar = inject(SnackbarService);
  protected readonly loader = inject(LoadingService);

  protected readonly countries = COUNTRIES;

  protected form = this.fb.group({
    companyName: ['', Validators.required],
    landlordCode: ['', Validators.required],
    country: ['ZA', Validators.required],
    registrationNumber: [''],
    address: ['', Validators.required],
  });

  private place: PlaceResult | null = null;
  private geometry: any = null;

  protected logoFile: File | null = null;

  generateCode(): void {
    const name = this.form.controls.companyName.value ?? '';
    this.form.controls.landlordCode.setValue(
      name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    );
  }

  onLogoSelected(event: Event): void {
    this.logoFile = (event.target as HTMLInputElement).files?.[0] ?? null;
  }

  clearLogoFile(): void {
    this.logoFile = null;
  }

  onAutocompleteSelected(place: PlaceResult): void {
    this.place = place;
  }

  onLocationSelected(location: any): void {
    this.geometry = location;
  }

  onSubmit(): void {
    if (this.form.invalid || !this.place || !this.geometry) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue();
    const {address, addressPlace} = toAdminAddress(this.addressService.parseJsonAddressData(this.geometry, this.place));

    this.setupService.createLandlord({
      companyName: v.companyName!,
      landlordCode: v.landlordCode!,
      country: v.country!,
      registrationNumber: v.registrationNumber || undefined,
      address,
      addressPlace,
    })
      .pipe(
        switchMap(({uuid}) =>
          this.logoFile
            ? this.setupService.uploadLandlordLogo(uuid, this.logoFile).pipe(switchMap(() => of(uuid)))
            : of(uuid)
        ),
        this.loader.withLoader()
      )
      .subscribe({
        next: (uuid) => this.dialogRef.close(uuid),
        error: () => this.snackbar.openSnackBar('Failed to create landlord. Please try again.'),
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
